import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { api } from "../utils/api";
import CitySearchInput from "./CitySearchInput";

export const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Icônes vectorielles remplaçant les glyphes texte "✕"/"✓" et l'emoji "🙋" — jamais d'emoji
// comme élément d'interface ailleurs dans l'app, ce module faisait exception.
const CloseIcon = ({ size = 20, color = "#999" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const CheckIcon = ({ size = 14, color = "#0a0a0a" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CheckCircleIcon = ({ size = 14, color = "#0a0a0a" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
export const MegaphoneIcon = ({ size = 26, color = "#999" }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l18-5v12L3 13v-2z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </svg>
);

export type PowerRequest = {
  id: string; user_id: string; title: string; description: string; category: string; price: string; is_free: boolean;
  location_name?: string; location_type?: string; created_at: string;
  author_name: string; author_avatar?: string; author_score: string; offers_count: string;
};

// Modals partagées entre la page globale "Demandes" (/power-requests) et la page d'un lieu
// (/venue/:id/prestataires) — seule la liste de catégories proposées et le venue_id rattaché
// à la création changent selon le contexte d'où on arrive.
export function CreateRequestModal({ categories, venueId, venueName, onClose, onCreated }: {
  categories: string[]; venueId?: string; venueName?: string; onClose: () => void; onCreated: () => void;
}) {
  const [category, setCategory] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [locationType, setLocationType] = useState<"home" | "provider" | "remote">("home");
  const [locationName, setLocationName] = useState("");
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = category !== "" && title.trim() !== "" && description.trim() !== "";

  const submit = () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    api.post("/publications", {
      type: "power_request", category, title: title.trim(), description: description.trim(),
      price: budget ? Number(budget) : 0, is_free: !budget, location_type: locationType, audience: "public",
      venue_id: venueId || undefined,
      location_name: locationName || undefined, latitude: coords?.lat, longitude: coords?.lon,
    }).then(data => {
      if (data?.publication) onCreated();
      else alert(data?.error || "Erreur lors de la publication");
    }).catch(() => alert("Erreur de connexion")).finally(() => setSubmitting(false));
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>Publier une demande</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex" }}><CloseIcon /></button>
        </div>
        {venueName && <div style={{ fontSize: 12.5, color: "#999", marginBottom: 16 }}>Chez {venueName}</div>}
        {!venueName && <div style={{ marginBottom: 12 }} />}

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Catégorie</label>
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 14, backgroundColor: "white", boxSizing: "border-box" }}>
          <option value="">Choisir...</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Titre</label>
        <input value={title} onChange={e => setTitle(e.target.value)} maxLength={100} placeholder="Ex: Cherche bricoleur pour monter une étagère" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 14, boxSizing: "border-box" }} />

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} maxLength={500} placeholder="Décris ce dont tu as besoin..." style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 14, boxSizing: "border-box", resize: "none" as const }} />

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Budget indicatif (optionnel)</label>
        <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E0E0E0", borderRadius: 10, padding: "2px 12px", marginBottom: 14, boxSizing: "border-box" as const }}>
          <input value={budget} onChange={e => setBudget(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" style={{ flex: 1, border: "none", outline: "none", fontSize: 14, padding: "10px 0", boxSizing: "border-box" as const }} />
          <span style={{ fontSize: 15, fontWeight: 700, color: "#999" }}>€</span>
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Lieu</label>
        <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" as const }}>
          {([{ key: "home", label: "Chez moi" }, { key: "provider", label: "Chez le prestataire" }, { key: "remote", label: "À distance" }] as const).map(o => (
            <button key={o.key} onClick={() => setLocationType(o.key)} style={{ padding: "8px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: locationType === o.key ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: locationType === o.key ? "#0a0a0a" : "white", color: locationType === o.key ? "white" : "#666" }}>
              {o.label}
            </button>
          ))}
        </div>

        <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Adresse (optionnel)</label>
        <div style={{ marginBottom: 18 }}>
          <CitySearchInput
            city={locationName}
            onChangeText={setLocationName}
            onSelect={(label, lat, lon) => { setLocationName(label); setCoords({ lat, lon }); }}
            placeholder="Ex : Bandol, Var ou Milan"
          />
        </div>

        <button onClick={submit} disabled={!canSubmit || submitting} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (!canSubmit || submitting) ? 0.5 : 1 }}>
          {submitting ? "Publication..." : "Publier la demande"}
        </button>
      </div>
    </div>,
    document.body
  );
}

export function RequestDetailModal({ request, isOwner, onClose, onChanged }: { request: PowerRequest; isOwner: boolean; onClose: () => void; onChanged: () => void }) {
  const [offers, setOffers] = useState<any[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(isOwner);
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  // Vue prestataire : sans ça, fermer puis rouvrir la fiche perdait tout souvenir du devis déjà
  // envoyé (le formulaire "Faire un devis" réapparaissait comme si de rien n'était) — impossible
  // de voir/répondre à une contre-proposition dans ces conditions.
  const [mine, setMine] = useState<any>(null);
  const [loadingMine, setLoadingMine] = useState(!isOwner);
  // Négociation d'un devis reçu (côté demandeur) — "Autre" révèle Refuser/Négocier au lieu d'un
  // unique bouton Accepter.
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [negotiatingId, setNegotiatingId] = useState<string | null>(null);
  const [counterPrice, setCounterPrice] = useState("");
  const [negotiating, setNegotiating] = useState(false);
  const [respondingNegotiation, setRespondingNegotiation] = useState(false);

  useEffect(() => {
    if (isOwner) {
      api.get(`/publications/${request.id}/offers`).then(data => setOffers(data?.offers || [])).catch(() => {}).finally(() => setLoadingOffers(false));
    } else {
      api.get(`/publications/${request.id}/offers/mine`).then(data => setMine(data?.offer || null)).catch(() => {}).finally(() => setLoadingMine(false));
    }
  }, [isOwner, request.id]);

  const submitOffer = () => {
    if (!price || submitting) return;
    setSubmitting(true);
    api.post(`/publications/${request.id}/offers`, { price: Number(price), message: message.trim() || undefined })
      .then(data => { if (data?.offer) { setSent(true); setMine(data.offer); onChanged(); } else alert(data?.error || "Erreur"); })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSubmitting(false));
  };

  const acceptOffer = (offerId: string) => {
    if (!window.confirm("Accepter ce devis ? La demande sera fermée aux autres.")) return;
    api.put(`/publications/${request.id}/offers/${offerId}/accept`, {}).then(() => { onChanged(); onClose(); }).catch(() => {});
  };

  const declineOffer = (offerId: string) => {
    if (!window.confirm("Refuser ce devis ? Le prestataire sera prévenu.")) return;
    api.put(`/publications/${request.id}/offers/${offerId}/decline`, {})
      .then(data => { if (data?.message) setOffers(prev => prev.map(o => o.id === offerId ? { ...o, status: "declined" } : o)); })
      .catch(() => {});
  };

  const sendNegotiation = (offerId: string) => {
    if (!counterPrice || negotiating) return;
    setNegotiating(true);
    api.put(`/publications/${request.id}/offers/${offerId}/negotiate`, { price: Number(counterPrice) })
      .then(data => {
        if (data?.offer) { setOffers(prev => prev.map(o => o.id === offerId ? data.offer : o)); setNegotiatingId(null); setCounterPrice(""); }
        else alert(data?.error || "Erreur");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setNegotiating(false));
  };

  const respondToNegotiation = (accept: boolean) => {
    if (!mine || respondingNegotiation) return;
    setRespondingNegotiation(true);
    api.put(`/publications/${request.id}/offers/${mine.id}/negotiation/${accept ? "accept" : "decline"}`, {})
      .then(data => {
        if (accept && data?.offer) { setMine(data.offer); onChanged(); }
        else if (!accept) { setMine(null); onChanged(); } // "aucune trace", l'échange disparaît aussi de cet écran
        else alert(data?.error || "Erreur");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setRespondingNegotiation(false));
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{request.title}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex" }}><CloseIcon /></button>
        </div>
        <div style={{ fontSize: 12, color: "#999", marginBottom: 14 }}>{request.category} · {request.author_name}</div>
        <div style={{ fontSize: 14, color: "#333", lineHeight: 1.6, marginBottom: 18 }}>{request.description}</div>

        {isOwner ? (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Devis reçus</div>
            {loadingOffers ? (
              <div style={{ fontSize: 13, color: "#999" }}>Chargement...</div>
            ) : offers.length === 0 ? (
              <div style={{ fontSize: 13, color: "#999" }}>Aucun devis pour l'instant.</div>
            ) : (
              offers.map(o => (
                <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F5F5F5", flexWrap: "wrap" as const }}>
                  <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                    {o.provider_avatar ? <img src={o.provider_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(o.provider_name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{o.provider_name}, {o.price} €</div>
                    {o.message && <div style={{ fontSize: 12, color: "#999" }}>{o.message}</div>}
                    {o.counter_status === "pending" && (
                      <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>Contre-proposition envoyée : {o.counter_price} €, en attente de réponse</div>
                    )}
                  </div>
                  {o.status === "accepted" ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: "#0a0a0a" }}><CheckCircleIcon size={13} /> Accepté</span>
                  ) : o.status === "declined" ? (
                    <span style={{ fontSize: 11, color: "#bbb" }}>Non retenu</span>
                  ) : o.counter_status === "pending" ? (
                    <span style={{ fontSize: 11, color: "#999" }}>⏳</span>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <button onClick={() => acceptOffer(o.id)} style={{ padding: "6px 12px", borderRadius: 16, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Accepter</button>
                      <div style={{ position: "relative" }}>
                        <button onClick={() => setMenuOpenFor(v => v === o.id ? null : o.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#999", textDecoration: "underline", padding: 0 }}>Autre</button>
                        {menuOpenFor === o.id && (
                          <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 6, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 10, boxShadow: "0 4px 16px rgba(0,0,0,0.12)", zIndex: 10, minWidth: 160, overflow: "hidden" }}>
                            <button onClick={() => { setMenuOpenFor(null); declineOffer(o.id); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#c0392b" }}>Refuser</button>
                            <button onClick={() => { setMenuOpenFor(null); setNegotiatingId(o.id); setCounterPrice(""); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "10px 14px", border: "none", background: "none", borderTop: "1px solid #F5F5F5", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>Négocier</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {negotiatingId === o.id && (
                    <div style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E0E0E0", borderRadius: 10, padding: "2px 12px", flex: 1, boxSizing: "border-box" as const }}>
                        <input autoFocus value={counterPrice} onChange={e => setCounterPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Ton prix" style={{ flex: 1, border: "none", outline: "none", fontSize: 14, padding: "10px 0", boxSizing: "border-box" as const }} />
                        <span style={{ fontSize: 15, fontWeight: 700, color: "#999" }}>€</span>
                      </div>
                      <button onClick={() => sendNegotiation(o.id)} disabled={!counterPrice || negotiating} style={{ padding: "8px 14px", borderRadius: 16, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 11.5, fontWeight: 700, cursor: "pointer", opacity: (!counterPrice || negotiating) ? 0.6 : 1 }}>
                        {negotiating ? "..." : "Envoyer"}
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        ) : loadingMine ? (
          <div style={{ fontSize: 13, color: "#999" }}>Chargement...</div>
        ) : mine?.counter_status === "pending" ? (
          <div style={{ padding: "10px 0" }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Contre-proposition reçue</div>
            <div style={{ fontSize: 14, marginBottom: 4 }}>{request.author_name} te propose <b>{mine.counter_price} €</b> au lieu de {mine.price} €.</div>
            {!!mine.counter_message && <div style={{ fontSize: 12.5, color: "#999", marginBottom: 10 }}>{mine.counter_message}</div>}
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button onClick={() => respondToNegotiation(false)} disabled={respondingNegotiation} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: "white", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>Refuser</button>
              <button onClick={() => respondToNegotiation(true)} disabled={respondingNegotiation} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 14, fontWeight: 600, opacity: respondingNegotiation ? 0.6 : 1 }}>
                {respondingNegotiation ? "..." : "Accepter"}
              </button>
            </div>
          </div>
        ) : mine?.status === "accepted" ? (
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <CheckCircleIcon size={22} color="white" />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Devis accepté à {mine.price} € !</div>
          </div>
        ) : mine?.status === "declined" ? (
          <div style={{ padding: "20px 0", textAlign: "center", color: "#999", fontSize: 14, fontWeight: 600 }}>Ton devis n'a pas été retenu.</div>
        ) : sent || (mine && mine.status === "pending") ? (
          <div style={{ padding: "20px 0", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}>
              <CheckIcon size={22} color="white" />
            </div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Devis envoyé{mine ? ` : ${mine.price} €` : ""}</div>
            <div style={{ fontSize: 12.5, color: "#999", marginTop: 4 }}>En attente de réponse.</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Faire un devis</div>
            {/* Valeur + "€" toujours visibles ensemble — avant, le "€" n'existait que dans le
                placeholder et disparaissait dès la première frappe. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, border: "1px solid #E0E0E0", borderRadius: 10, padding: "2px 12px", marginBottom: 10, boxSizing: "border-box" as const }}>
              <input value={price} onChange={e => setPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder="0" style={{ flex: 1, border: "none", outline: "none", fontSize: 14, padding: "10px 0", boxSizing: "border-box" as const }} />
              <span style={{ fontSize: 15, fontWeight: 700, color: "#999" }}>€</span>
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={300} placeholder="Un mot pour te présenter (optionnel)..." style={{ width: "100%", minHeight: 60, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 14, boxSizing: "border-box", resize: "none" as const }} />
            <button onClick={submitOffer} disabled={!price || submitting} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (!price || submitting) ? 0.5 : 1 }}>
              {submitting ? "Envoi..." : "Envoyer le devis"}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
